import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function SignupPage() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);
    const { error: signUpError } = await signUp(email, password, fullName);
    setLoading(false);
    if (signUpError) {
      setError(signUpError);
    } else {
      setSuccessMessage('Account created! Check your email for a confirmation link, then sign in.');
    }
  };

  return (
    <div className="flex min-h-screen bg-cream dark:bg-midnight-deep">
      {/* Left brand panel — shared with Login */}
      <div
        className="hidden lg:flex lg:w-1/2 items-center justify-center relative overflow-hidden"
        style={{ background: 'linear-gradient(to bottom, #0F172A 0%, #0A0A0F 100%)' }}
      >
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
            backgroundSize: '22px 22px',
          }}
        />
        <div className="absolute top-6 left-8 inline-flex items-center gap-1.5">
          <i className="ri-terminal-box-line text-electric text-sm" />
          <span className="text-[11px] font-medium text-electric">by CodeLab</span>
        </div>

        <div className="relative z-10 text-center px-12">
          <img
            src="/icon-192.png"
            alt="Zman logo"
            className="w-[72px] h-[72px] mx-auto mb-6 rounded-2xl"
            style={{ boxShadow: '0 0 30px rgba(212,168,67,0.3)' }}
          />
          <h1 className="font-display text-[42px] font-bold text-white mb-1 tracking-tight">Zman</h1>
          <p className="text-base italic text-gold mb-6">Your first streak starts today.</p>

          <div className="w-10 h-px bg-gold mx-auto mb-6" />

          <ul className="inline-flex flex-col gap-3.5 text-left">
            {[
              'Hebrew calendar & Zmanim',
              'Automated daily phone reminders',
              'Streak tracking',
              'Shabbat & holiday aware',
            ].map((feature) => (
              <li key={feature} className="flex items-center gap-2.5 text-white text-sm">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center">
                  <i className="ri-check-line text-gold text-xs" />
                </span>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        <div className="absolute bottom-6 left-8 inline-flex items-center gap-1.5 text-[11px] text-slate-500">
          <i className="ri-terminal-box-line text-electric text-xs" />
          Powered by CodeLab
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-[380px]">
          <div className="lg:hidden text-center mb-8">
            <img src="/icon-192.png" alt="Zman logo" className="w-14 h-14 mx-auto mb-4 rounded-xl" />
            <h1 className="font-display text-2xl font-bold text-foreground-950">Zman</h1>
            <p className="text-sm text-gold">Your Time. Your Habits.</p>
          </div>

          <h2 className="font-display text-[28px] font-bold text-foreground-950 mb-1">Create your account</h2>
          <p className="text-sm text-foreground-500 mb-8">Set up your accountability profile</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            {successMessage && (
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
                {successMessage}
              </div>
            )}

            <div>
              <label htmlFor="fullName" className="block text-[13px] font-medium text-foreground-600 mb-1.5">
                Full Name
              </label>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                placeholder="Your full name"
                className="w-full px-4 py-3 text-sm rounded-xl border border-background-300 bg-surface dark:bg-midnight-card text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-[13px] font-medium text-foreground-600 mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                dir="ltr"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full px-4 py-3 text-sm rounded-xl border border-background-300 bg-surface dark:bg-midnight-card text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-[13px] font-medium text-foreground-600 mb-1.5">
                Password
              </label>
              <input
                id="password"
                type="password"
                dir="ltr"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="At least 6 characters"
                className="w-full px-4 py-3 text-sm rounded-xl border border-background-300 bg-surface dark:bg-midnight-card text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full h-12 flex items-center justify-center text-sm whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-foreground-500">
            Already have an account?{' '}
            <Link to="/login" className="text-electric hover:underline font-medium transition">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}